/**
 * Avatar constants
 * Defines available avatar options using lucide-react icons
 */

import {
  User,
  Swords,
  Shield,
  Skull,
  Crown,
  Flame,
  Zap,
  Heart,
  Star,
  Trophy,
  Target,
  Wrench,
  Rocket,
  Eye,
  Hammer,
  Sword,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface AvatarOption {
  /** Unique identifier for the avatar */
  id: string;
  /** Display name for the avatar */
  name: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Primary color for the avatar (using theme tokens) */
  color: string;
}

/**
 * Available avatar options
 * Each avatar uses a different lucide-react icon
 */
export const AVATAR_OPTIONS: AvatarOption[] = [
  {
    id: 'avatar-user',
    name: 'Soldier',
    icon: User,
    color: 'text-accent-primary',
  },
  {
    id: 'avatar-swords',
    name: 'Warrior',
    icon: Swords,
    color: 'text-accent-primary',
  },
  {
    id: 'avatar-shield',
    name: 'Defender',
    icon: Shield,
    color: 'text-accent-secondary',
  },
  {
    id: 'avatar-skull',
    name: 'Reaper',
    icon: Skull,
    color: 'text-text-muted',
  },
  {
    id: 'avatar-crown',
    name: 'Commander',
    icon: Crown,
    color: 'text-accent-primary',
  },
  {
    id: 'avatar-flame',
    name: 'Pyromancer',
    icon: Flame,
    color: 'text-semantic-error',
  },
  {
    id: 'avatar-zap',
    name: 'Psyker',
    icon: Zap,
    color: 'text-accent-tertiary',
  },
  {
    id: 'avatar-heart',
    name: 'Medic',
    icon: Heart,
    color: 'text-semantic-success',
  },
  {
    id: 'avatar-star',
    name: 'Champion',
    icon: Star,
    color: 'text-accent-primary',
  },
  {
    id: 'avatar-trophy',
    name: 'Victor',
    icon: Trophy,
    color: 'text-accent-primary',
  },
  {
    id: 'avatar-target',
    name: 'Marksman',
    icon: Target,
    color: 'text-accent-secondary',
  },
  {
    id: 'avatar-wrench',
    name: 'Engineer',
    icon: Wrench,
    color: 'text-text-secondary',
  },
  {
    id: 'avatar-rocket',
    name: 'Pilot',
    icon: Rocket,
    color: 'text-accent-tertiary',
  },
  {
    id: 'avatar-eye',
    name: 'Scout',
    icon: Eye,
    color: 'text-accent-tertiary',
  },
  {
    id: 'avatar-hammer',
    name: 'Smith',
    icon: Hammer,
    color: 'text-text-secondary',
  },
  {
    id: 'avatar-sword',
    name: 'Duelist',
    icon: Sword,
    color: 'text-accent-primary',
  },
];

/**
 * Get avatar option by ID
 */
export function getAvatarById(id: string | null | undefined): AvatarOption | null {
  if (!id) return null;
  return AVATAR_OPTIONS.find((avatar) => avatar.id === id) || null;
}

/**
 * Get default avatar option
 */
export function getDefaultAvatar(): AvatarOption {
  return AVATAR_OPTIONS[0]; // User icon
}
